import { fetcher } from '#lib/backendFetcher';
import { validateResponse } from '#lib/validation';

import { orgCreditsSchema, orgInfoSchema, orgTeamSchema } from './orgSchemas';

/**
 * Fetches the organization information of the logged-in user and validates it.
 * @returns {Promise<Object>}
 */
export const fetchOrgInfo = async () => {
	const data = await fetcher('/api/v1/platform/orgs/me');
	return validateResponse(orgInfoSchema, data, 'orgInfo');
};

/**
 * Ensures an organization (membership + wallet) exists for the current user.
 * Idempotent login self-heal — safe to call on every login. Returns the org id.
 * @returns {Promise<{orgId: string}>}
 */
export const ensureOrgExists = async () => {
	return await fetcher('/api/v1/platform/auth/ensure-org-exists', { method: 'POST' });
};

/**
 * Fetches all members of the organization (credit usage and limits flattened into each member) and, for
 * admins, the org's pending invites; members get an empty invite list.
 * @returns {Promise<{members: Array<Object>, invites: Array<Object>}>}
 */
export const fetchOrgMembers = async () => {
	const res = await fetcher('/api/v1/platform/orgs/members');
	const members = (res.members ?? []).map(({ wallet, ...member }) => ({
		...member,
		companyUsed: wallet?.companyUsed ?? 0,
		contactUsed: wallet?.contactUsed ?? 0,
		companyLimit: wallet?.companyLimit ?? null,
		contactLimit: wallet?.contactLimit ?? null,
	}));
	return validateResponse(orgTeamSchema, { members, invites: res.invites ?? [] }, 'orgMembers');
};

/**
 * Invites a new member to the organization by email.
 * @param {string} email - Email address of the user to invite
 * @param {string} role - Role of the new member (e.g. 'admin', 'member')
 * @returns {Promise<Object>}
 */
export const inviteMember = async (email, role) => {
	return await fetcher('/api/v1/platform/orgs/members/invite', {
		method: 'POST',
		body: { email, role },
	});
};

/**
 * Removes a member from the organization, optionally with data transfer.
 * @param {string} userId - ID of the member to remove
 * @param {boolean} [transferData=false] - Whether to transfer the member's data
 * @returns {Promise<Object>}
 */
export const removeMember = async (userId, transferData = false) => {
	return await fetcher(`/api/v1/platform/orgs/members/${userId}?transferData=${transferData}`, {
		method: 'DELETE',
	});
};

/**
 * Extends a pending org invite by 7 days and mails it again.
 * @param {string} inviteId
 * @returns {Promise<{expiresAt: string}>}
 */
export const resendInvite = async (inviteId) => {
	return await fetcher(`/api/v1/platform/orgs/members/invites/${inviteId}/resend`, {
		method: 'POST',
	});
};

/**
 * Withdraws a pending org invite; the backend also deletes the invitee's account if nothing else uses it.
 * @param {string} inviteId
 * @returns {Promise<{accountDeleted: boolean}>}
 */
export const revokeInvite = async (inviteId) => {
	return await fetcher(`/api/v1/platform/orgs/members/invites/${inviteId}`, {
		method: 'DELETE',
	});
};

/**
 * Fetches all pending invitations of the user.
 * @returns {Promise<Object>}
 */
export const fetchPendingInvites = async () => {
	return await fetcher('/api/v1/platform/orgs/invites');
};

/**
 * Accepts an organization invitation.
 * @param {string} inviteId - Invitation ID
 * @returns {Promise<Object>}
 */
export const acceptInvite = async (inviteId) => {
	return await fetcher(`/api/v1/platform/orgs/invites/${inviteId}/accept`, {
		method: 'POST',
	});
};

/**
 * Declines an organization invitation.
 * @param {string} inviteId - Invitation ID
 * @returns {Promise<Object>}
 */
export const declineInvite = async (inviteId) => {
	return await fetcher(`/api/v1/platform/orgs/invites/${inviteId}/decline`, {
		method: 'POST',
	});
};

/**
 * Sets a member's monthly credit limits; `null` lets the member use the org's pool freely.
 * @param {string} userId - Member ID
 * @param {{companyLimit: (number|null), contactLimit: (number|null)}} limits
 * @returns {Promise<{companyLimit: (number|null), contactLimit: (number|null)}>}
 */
export const setMemberLimits = async (userId, { companyLimit, contactLimit }) => {
	return await fetcher(`/api/v1/platform/orgs/members/${userId}/limit`, {
		method: 'PUT',
		body: { companyLimit, contactLimit },
	});
};

/**
 * Changes the role of an organization member.
 * @param {string} userId - Member ID
 * @param {string} role - New role (e.g. 'admin', 'member')
 * @returns {Promise<Object>}
 */
export const changeMemberRole = async (userId, role) => {
	return await fetcher(`/api/v1/platform/orgs/members/${userId}/role`, {
		method: 'PUT',
		body: { role },
	});
};

/**
 * Fetches the org's credit pool this period, each member's usage and limit, and the renewal date.
 * @returns {Promise<Object>}
 */
export const fetchOrgCredits = async () => {
	const data = await fetcher('/api/v1/platform/orgs/credits');
	return validateResponse(orgCreditsSchema, data, 'orgCredits');
};
